/**
 * Pages written for people who search: how to host, a party game, a group activity,
 * a multiplayer beat maker, and the landing page in Hebrew. Each FAQ lives in data,
 * so the page and its structured data (seo.ts) always say the same thing.
 */
import { useState, type ReactNode } from 'react';
import { useServerInfo } from './api.ts';
import { DonateButton, Footer, GUIDE_LINKS, HeroArt, Logo, OnAPhone, Page } from './Site.tsx';

export type Faq = { q: string; a: string }[];

function FaqList({ items, title = 'Questions' }: { items: Faq; title?: string }) {
  return (
    <section className="faq">
      <h2>{title}</h2>
      {items.map((f) => (
        <details key={f.q}>
          <summary>{f.q}</summary>
          <p>{f.a}</p>
        </details>
      ))}
    </section>
  );
}

function StartBox({ line }: { line: string }) {
  return (
    <aside className="guide-cta">
      <p>{line}</p>
      <a className="btn btn-primary btn-lg" href="/host">
        Start a jam (free)
      </a>
    </aside>
  );
}

function MoreGuides({ here }: { here: string }) {
  return (
    <nav className="more-guides" aria-label="More guides">
      <h2>More guides</h2>
      <ul>
        {GUIDE_LINKS.filter((g) => g.href !== here).map((g) => (
          <li key={g.href}>
            <a href={g.href}>{g.label}</a>
          </li>
        ))}
        <li>
          <a href="/">What is Combinator?</a>
        </li>
      </ul>
    </nav>
  );
}

function Guide({ path, title, lead, children, faq, cta }: { path: string; title: string; lead: string; children: ReactNode; faq: Faq; cta: string }) {
  const info = useServerInfo();
  return (
    <Page info={info}>
      <article className="guide">
        <nav className="crumbs" aria-label="Breadcrumb">
          <a href="/">Combinator</a> › <span>Guides</span>
        </nav>
        <h1>{title}</h1>
        <p className="lead">{lead}</p>
        {children}
        <StartBox line={cta} />
        <FaqList items={faq} />
        <MoreGuides here={path} />
      </article>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// How to host
// ---------------------------------------------------------------------------

export const HOST_STEPS: { name: string; text: string }[] = [
  { name: 'Open Combinator on the big screen', text: 'On a laptop or TV, go to combinator.club and press Start a jam. Only the host signs in, with a 6-digit code sent to their email. No password.' },
  { name: 'Plug in the best speakers in the room', text: 'All the sound comes out of this screen. A cable or HDMI feels tighter than Bluetooth, which adds delay.' },
  { name: 'Everyone scans the QR code', text: 'The code is on the screen. Phones open it with the camera: no app and no account. Wi-Fi or mobile data both work.' },
  { name: 'Start the beat', text: 'Press Play, or play one of the demos. Each phone picks an instrument: drums, bass, keys, the sampler or the effects pad.' },
  { name: 'Keep what you made', text: 'Record the jam into a song, save it to your account, download the song file, or export the audio. A share card sums up the night.' },
];

export const HOST_FAQ: Faq = [
  { q: 'How many people can play?', a: 'Every phone in the room. Eight players get their own colour on the big screen; more can join and share colours. Each instrument can have several players.' },
  { q: 'Do the guests need an account or an app?', a: 'No. Only the host signs in. Guests scan the QR code and play in the browser.' },
  { q: 'Can a TV or a phone be the host?', a: 'Yes. Combinator switches to "Smooth sound" on TVs and phones: a bigger audio buffer and a lighter mix, so it plays without crackles. A laptop is still the best host.' },
  { q: 'The sound crackles or stutters. What can I do?', a: 'Open the sound output window on the host and choose Smooth. Close other heavy tabs, and keep the host screen on: switching apps on a phone pauses the sound.' },
  { q: 'The host page reloaded. Is the jam gone?', a: 'No. The room, the song and the phones wait for it. Press "Bring the sound back" and everyone carries on.' },
];

export function HowToHost() {
  return (
    <Guide
      path="/how-to-host"
      title="How to host a jam with Combinator"
      lead="One screen plays the music and every phone in the room becomes an instrument. Here is how to set it up in two minutes, and what to do when something feels off."
      faq={HOST_FAQ}
      cta="Ready? It's free, and only the host signs in."
    >
      <h2>What you need</h2>
      <ul>
        <li>A laptop or a TV with a browser (Chrome, Edge or Safari).</li>
        <li>Speakers. A cable or HDMI is best; Bluetooth works but adds delay.</li>
        <li>The phones of everyone who wants to play. Any phone with a camera and a browser.</li>
        <li>Optional: a USB MIDI keyboard for the host.</li>
      </ul>
      <h2>Set it up in five steps</h2>
      <ol className="steps-list">
        {HOST_STEPS.map((s) => (
          <li key={s.name}>
            <b>{s.name}.</b> {s.text}
          </li>
        ))}
      </ol>
      <h2>Tips for a good night</h2>
      <ul>
        <li>Give each person a different instrument first. Drums and bass make a groove fast; keys and the effects pad come on top.</li>
        <li>Leave everyone in Easy mode at the start. Hits snap to the beat and the keys stay in the song's key, so nothing sounds wrong.</li>
        <li>Stuck for a start? Play a demo and let people play along with it.</li>
        <li>Give the effects pad to the person who says "I can't play anything". Moving a finger across a filter is the most fun job in the room.</li>
        <li>Press "Record to song" when it sounds good, and play the song back at the end.</li>
      </ul>
      <h2>When something's off</h2>
      <ul>
        <li><b>A phone can't open the link:</b> check that it has internet, then scan again, or open combinator.club on it and type the four-letter code.</li>
        <li><b>The sound comes late:</b> use wired speakers. Bluetooth adds a delay everyone can feel.</li>
        <li><b>Crackles on a TV or phone host:</b> choose Smooth in the sound output window.</li>
        <li><b>Someone stopped the music by accident:</b> on phones, stop needs a short hold, so that rarely happens. Any phone can start the beat again.</li>
      </ul>
    </Guide>
  );
}

// ---------------------------------------------------------------------------
// Party game
// ---------------------------------------------------------------------------

export const PARTY_FAQ: Faq = [
  { q: 'Do people need to know how to play music?', a: 'No. In Easy mode every hit lands on the beat and every note fits the key. People who have never played make a beat in a few minutes.' },
  { q: 'Is it free?', a: 'Yes. No ads and no paywall. Only the host signs in, with an email code.' },
  { q: 'What if the Wi-Fi is bad?', a: 'Phones can join on mobile data too. The sound comes from the host screen, so the phones only send small messages.' },
  { q: 'Can we keep the song from the party?', a: 'Yes. Record into a song, export the audio, and share the end-of-jam card with everyone who played.' },
];

export function PartyGame() {
  return (
    <Guide
      path="/party-music-game"
      title="A music game for parties where everyone plays"
      lead="Instead of fighting over the playlist, the whole room makes the music. Each phone becomes an instrument, one speaker plays it all, and nobody needs to know how to play."
      faq={PARTY_FAQ}
      cta="Put it on the TV, pass the QR around, and see who becomes the drummer."
    >
      <h2>Why it works at a party</h2>
      <ul>
        <li><b>Nothing to install.</b> Guests scan a QR code with their camera and they're in.</li>
        <li><b>No wrong notes.</b> Easy mode snaps hits to the beat and keeps the keys in one key.</li>
        <li><b>One sound system.</b> Every phone plays through the same speakers, in time with each other.</li>
        <li><b>You can see who's playing.</b> Every hit shows on the big screen in that player's colour, with their name.</li>
      </ul>
      <h2>Ideas for the night</h2>
      <ul>
        <li><b>Build the drop:</b> start with only drums, add the bass after eight bars, then keys, then the effects pad sweeps it all.</li>
        <li><b>Beat relay:</b> pass the drums from phone to phone every minute. Each player adds one sound to the loop.</li>
        <li><b>Demo karaoke:</b> start a demo and let everyone play along on top of it.</li>
        <li><b>The finale:</b> record the best five minutes into a song and play it back at full volume.</li>
      </ul>
      <h2>Set up in two minutes</h2>
      <p>
        Open combinator.club on the laptop or TV, connect it to the speakers, and show the QR code. The <a href="/how-to-host">hosting guide</a> covers speakers, Wi-Fi and TVs.
      </p>
    </Guide>
  );
}

// ---------------------------------------------------------------------------
// Groups: classes, teams, workshops
// ---------------------------------------------------------------------------

export const GROUP_FAQ: Faq = [
  { q: 'Do participants need accounts?', a: 'No. Only the person running the session signs in. Participants join with a QR code and nothing about them is stored: their name exists only while the jam runs.' },
  { q: 'What age is it for?', a: 'Anyone who can tap a phone screen can play. The host account is for people 16 and older.' },
  { q: 'Do we need special equipment?', a: 'One computer or a smart TV connected to speakers, and the phones people already have. Phones without a data plan can use the Wi-Fi.' },
  { q: 'Can we keep the result?', a: 'Yes. Save the song, download the audio, or share the end-of-session card with the group.' },
];

export function GroupActivity() {
  return (
    <Guide
      path="/group-music-activity"
      title="A music activity for classes, teams and workshops"
      lead="Everyone in the group plays at the same time, on the phones they already have, and it sounds good from the first minute. No instruments and no experience needed."
      faq={GROUP_FAQ}
      cta="Try it before the session: open a jam and join it with your own phone."
    >
      <h2>Why it works with a group</h2>
      <ul>
        <li><b>Everyone takes part at once.</b> No waiting for a turn on the one keyboard in the room.</li>
        <li><b>Instant success.</b> Easy mode keeps every hit on the beat and every note in key.</li>
        <li><b>Real musical ideas.</b> Beat, bass line, chords, key and scale, arrangement: the group meets them by playing, then sees them on the big screen.</li>
        <li><b>Private by design.</b> Participants don't sign up, and no data from their phones is stored.</li>
      </ul>
      <h2>A 40-minute session plan</h2>
      <ol className="steps-list">
        <li><b>Warm-up (5 min).</b> Everyone on drums. Clap a simple rhythm together, then play it on the pads.</li>
        <li><b>The groove (10 min).</b> Split the group: drums, bass, keys and effects. Start a demo or a starter beat, and let each group add one part.</li>
        <li><b>Key and chords (10 min).</b> Change the key on the host: every phone's keyboard follows it, so what people add still fits. Try the Chords layout on the keys.</li>
        <li><b>Build a song (10 min).</b> Record the jam into the song timeline: an intro, a part where everything plays, and a break.</li>
        <li><b>Listen back (5 min).</b> Play the song for the room and share the end-of-session card.</li>
      </ol>
      <h2>For team building</h2>
      <p>
        Give each team one instrument and one minute to add something to the loop, then switch. It's quick, it's loud, and it needs listening to each other more than talent. The
        result is a track the team made together.
      </p>
      <p>
        Setting up for the first time? Read <a href="/how-to-host">how to host a jam</a>.
      </p>
    </Guide>
  );
}

// ---------------------------------------------------------------------------
// Multiplayer beat maker
// ---------------------------------------------------------------------------

export const BEAT_FAQ: Faq = [
  { q: 'Is it really free?', a: 'Yes. No ads, no paywall and no watermark. Only the host signs in.' },
  { q: 'Can I use the built-in sounds in my own releases?', a: 'Yes. Audio you record with Combinator, including the built-in sounds, can go into your own music, including commercial releases. You may not redistribute the sounds themselves as a sample pack.' },
  { q: 'What can I export?', a: 'The audio of the mix (recorded as you play, plus the last five minutes kept in the background on a laptop), and a song file that opens again in Combinator.' },
  { q: 'Does it work with a MIDI keyboard?', a: 'Yes, on the host screen in Chrome or Edge: pads from C1, keys as they are.' },
];

export function BeatMaker() {
  return (
    <Guide
      path="/multiplayer-beat-maker"
      title="A multiplayer beat maker in the browser"
      lead="Make beats together in real time: a pattern sequencer, piano roll, song timeline and mixer on the big screen, and every friend editing from their own phone."
      faq={BEAT_FAQ}
      cta="Open a jam and send the QR to whoever's in the room."
    >
      <h2>What's inside</h2>
      <ul>
        <li><b>Patterns:</b> every track has A, B, C… patterns of 1 to 16 bars, with a step grid on the phone.</li>
        <li><b>Piano roll:</b> draw, select, move and resize notes, with velocity per note, snap from 1/4 to 1/32 and triplets, quantize, transpose.</li>
        <li><b>Song timeline:</b> clips, fades, mute, split, and automation lanes you can record from the effects pad or draw by hand.</li>
        <li><b>Sounds:</b> sampled drum kits, synth bass and keys with presets, wavetables, sampled instruments, and a sampler you can fill from the phone's microphone.</li>
        <li><b>Mixer:</b> volume and pan per channel and per drum voice, a 6-band EQ and a compressor with a tube mode.</li>
        <li><b>Effects pad:</b> filter, delay, drive and reverb on an XY pad, with motion loops.</li>
      </ul>
      <h2>Made for playing together</h2>
      <ul>
        <li>Every phone is locked to one clock, so live hits from different phones land together.</li>
        <li>Edits from any phone show up everywhere at once, and each player has their own undo.</li>
        <li>Easy mode for friends who don't produce; Advanced for free timing, velocity and every note.</li>
      </ul>
      <h2>Good for</h2>
      <ul>
        <li>Sketching ideas with friends in the studio or on the couch.</li>
        <li>A live set where the crowd plays the drums.</li>
        <li>Showing someone how a beat is built, one track at a time.</li>
      </ul>
    </Guide>
  );
}

// ---------------------------------------------------------------------------
// The landing page in Hebrew
// ---------------------------------------------------------------------------

export const HE_FAQ: Faq = [
  { q: 'כולם צריכים להירשם?', a: 'לא. רק מי שפותח את הג׳אם נרשם, עם קוד שנשלח למייל (בלי סיסמה). הטלפונים מצטרפים בסריקת QR.' },
  { q: 'מה צריך?', a: 'מחשב או טלוויזיה עם דפדפן (Chrome, ‏Edge או Safari), רמקולים, וטלפונים. רמקולים בכבל או ב-HDMI מרגישים מדויקים יותר מבלוטות׳.' },
  { q: 'הטלפונים צריכים להיות על אותו Wi-Fi?', a: 'עדיף, אבל לא חובה: אפשר להצטרף גם מהגלישה הסלולרית. על אותו Wi-Fi ההשהיה הכי קטנה.' },
  { q: 'צריך לדעת לנגן?', a: 'לא. במצב Easy כל מכה נופלת על הקצב וכל תו מתאים לסולם, אז אין תווים לא נכונים.' },
  { q: 'אפשר לשמור את מה שיצרנו?', a: 'כן. שומרים את השיר בחשבון, מורידים קובץ שיר, ומייצאים את הסאונד.' },
  { q: 'זה עולה כסף?', a: 'לא. חינם, בלי פרסומות ובלי מעקב.' },
];

export function LandingHe() {
  const info = useServerInfo();
  const [code, setCode] = useState('');
  const go = () => code.trim().length >= 4 && location.assign(`/j/${code.trim().toUpperCase()}`);
  return (
    <div className="site landing-page" dir="rtl" lang="he">
      <section className="hero">
        <Logo size="big" />
        <h1 className="hero-title">
          Combinator
          <span className="promise">מסך אחד מנגן את המוזיקה. כל טלפון בחדר הופך לכלי נגינה.</span>
        </h1>
        <p className="sub">חינם, בדפדפן. החברים מצטרפים בסריקת QR: בלי אפליקציה ובלי הרשמה.</p>
        <a className="btn btn-primary btn-xl" href="/host">
          פתחו ג׳אם (חינם)
        </a>
        <OnAPhone he />
        <form
          className="join-code"
          onSubmit={(e) => {
            e.preventDefault();
            go();
          }}
        >
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
            placeholder="CODE"
            autoCapitalize="characters"
            aria-label="קוד הג׳אם"
            dir="ltr"
          />
          <button className="btn" type="submit" disabled={code.length < 4}>
            הצטרפות עם קוד
          </button>
        </form>
        <HeroArt />
        {/* /?lang=en: the server remembers it, so a Hebrew phone isn't sent back here */}
        <a className="lang-link" href="/?lang=en" hrefLang="en" lang="en">
          English
        </a>
      </section>

      <section className="how" aria-label="איך זה עובד">
        <div className="how-step">
          <span className="how-n">1</span>
          <h3>מסך גדול</h3>
          <p>פותחים את Combinator במחשב או בטלוויזיה ומחברים לרמקולים הכי טובים בחדר.</p>
        </div>
        <div className="how-step">
          <span className="how-n">2</span>
          <h3>סורקים</h3>
          <p>כולם סורקים את ה-QR שעל המסך. כל טלפון הופך לתופים, בס, קלידים, סמפלר או אפקטים.</p>
        </div>
        <div className="how-step">
          <span className="how-n">3</span>
          <h3>מנגנים</h3>
          <p>המכות נופלות על הקצב, בסולם. מקליטים את הג׳אם לשיר, עורכים ומייצאים.</p>
        </div>
      </section>

      <section className="features">
        <div>
          <h3>🥁 בנוי לחדר אחד</h3>
          <p>כל הטלפונים מנגנים דרך מערכת סאונד אחת, על שעון אחד. לא צריך אוזניות.</p>
        </div>
        <div>
          <h3>🎉 למסיבה, לכיתה, לגיבוש</h3>
          <p>כולם מנגנים בבת אחת, גם מי שאף פעם לא החזיק כלי. יש גם דמואים מוכנים להתחלה.</p>
        </div>
        <div>
          <h3>🎹 סקוונסר אמיתי</h3>
          <p>תבניות, פסנתר רול, ציר זמן של שיר, אוטומציה ומיקסר עם EQ וקומפרסור, במחשב ובכל טלפון.</p>
        </div>
        <div>
          <h3>🔊 סאונדים כלולים</h3>
          <p>ערכות תופים, לופים, כלים ו-wavetables, חופשיים לשימוש במוזיקה שאתם יוצרים.</p>
        </div>
      </section>

      <FaqList items={HE_FAQ} title="שאלות" />

      <section className="closing">
        <h2>מוכנים כשהחדר מוכן</h2>
        <a className="btn btn-primary btn-xl" href="/host">
          פתחו ג׳אם (חינם)
        </a>
        <p className="hint">הממשק עצמו באנגלית, בכוונה פשוט: כפתורים גדולים ומעט מילים.</p>
        {info?.donateUrl && (
          <p className="hint">
            חינם, בלי פרסומות. אם נהניתם, <DonateButton url={info.donateUrl} className="link-donate">קפה עוזר לשרת לרוץ</DonateButton>.
          </p>
        )}
      </section>
      <Footer info={info} />
    </div>
  );
}

/** Unknown address: say so (the server answers 404), and point somewhere useful. */
export function NotFound() {
  const info = useServerInfo();
  return (
    <Page info={info}>
      <article className="guide">
        <h1>This page isn't here</h1>
        <p className="lead">The link may be old or mistyped. Looking for a jam? Ask the host for the four-letter code, or start your own.</p>
        <p>
          <a className="btn btn-primary btn-lg" href="/">
            Go to Combinator
          </a>
        </p>
        <MoreGuides here="" />
      </article>
    </Page>
  );
}
